import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Settings,
  Users,
  Key,
  Plus,
  Trash2,
  Copy,
  RefreshCw,
  Loader2,
  Shield,
  BarChart3,
  LogOut,
  Eye,
  EyeOff,
  Wrench,
  Bell,
  RotateCcw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel({ onClose }) {
  const [adminKey, setAdminKey] = useState(localStorage.getItem("sib_admin_key") || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  const [loading, setLoading] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState(null);
  
  // Whitelist state
  const [whitelist, setWhitelist] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  
  // Invite codes state
  const [inviteCodes, setInviteCodes] = useState([]);
  const [newCodeName, setNewCodeName] = useState("");
  const [newCodeMaxUses, setNewCodeMaxUses] = useState(1);
  
  // Stats state
  const [stats, setStats] = useState(null);

  const authenticate = async () => {
    if (!adminKey.trim()) {
      toast.error("Please enter admin password");
      return;
    }
    
    setLoading(true);
    try {
      await axios.get(`${API}/admin/settings?admin_key=${adminKey}`);
      localStorage.setItem("sib_admin_key", adminKey);
      setIsAuthenticated(true);
      toast.success("Admin authenticated");
      loadData();
    } catch (error) {
      toast.error("Invalid admin credentials");
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsRes, whitelistRes, codesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/settings?admin_key=${adminKey}`),
        axios.get(`${API}/admin/whitelist?admin_key=${adminKey}`),
        axios.get(`${API}/admin/invite-codes?admin_key=${adminKey}`),
        axios.get(`${API}/admin/usage-stats?admin_key=${adminKey}`)
      ]);
      
      setSettings(settingsRes.data);
      setWhitelist(whitelistRes.data);
      setInviteCodes(codesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const response = await axios.put(
        `${API}/admin/settings?admin_key=${adminKey}`,
        updates
      );
      setSettings(response.data);
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const addWhitelistEntry = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter an email");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/whitelist?admin_key=${adminKey}`,
        { email: newEmail, name: newName || null }
      );
      setNewEmail("");
      setNewName("");
      loadData();
      toast.success("Email added to whitelist");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add email");
    }
  };

  const removeWhitelistEntry = async (email) => {
    try {
      await axios.delete(`${API}/admin/whitelist/${email}?admin_key=${adminKey}`);
      loadData();
      toast.success("Email removed from whitelist");
    } catch (error) {
      toast.error("Failed to remove email");
    }
  };

  const createInviteCode = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/invite-codes?admin_key=${adminKey}`,
        { name: newCodeName || null, max_uses: newCodeMaxUses }
      );
      setNewCodeName("");
      setNewCodeMaxUses(1);
      loadData();
      toast.success(`Created invite code: ${response.data.code}`);
      
      // Copy to clipboard
      navigator.clipboard.writeText(response.data.code);
      toast.info("Code copied to clipboard");
    } catch (error) {
      toast.error("Failed to create invite code");
    }
  };

  const deleteInviteCode = async (code) => {
    try {
      await axios.delete(`${API}/admin/invite-codes/${code}?admin_key=${adminKey}`);
      loadData();
      toast.success("Invite code deleted");
    } catch (error) {
      toast.error("Failed to delete invite code");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  const logout = () => {
    localStorage.removeItem("sib_admin_key");
    setIsAuthenticated(false);
    setAdminKey("");
    toast.success("Logged out");
  };

  // Auth screen
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#1F3A5F] flex items-center justify-center">
              <Shield className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#1F3A5F]">Admin Panel</h2>
              <p className="text-sm text-gray-500">Enter admin password to continue</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="relative">
              <Input
                type={showKey ? "text" : "password"}
                placeholder="Admin password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && authenticate()}
                className="pr-10 rounded-xl"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={authenticate}
                disabled={loading}
                className="flex-1 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75]"
                data-testid="admin-login-button"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Login"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin panel
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full shadow-xl my-8" data-testid="admin-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1F3A5F] flex items-center justify-center">
              <Settings className="text-white" size={20} />
            </div>
            <h2 className="text-xl font-bold text-[#1F3A5F]">Admin Panel</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadData}
              disabled={loading}
              className="text-gray-500"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-gray-500"
            >
              <LogOut size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-500"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-2 bg-gray-50 mx-4 mt-4 rounded-xl">
          {[
            { id: "settings", icon: Settings, label: "Mode" },
            { id: "whitelist", icon: Users, label: "Whitelist" },
            { id: "codes", icon: Key, label: "Invite Codes" },
            { id: "stats", icon: BarChart3, label: "Stats" },
            { id: "tools", icon: Wrench, label: "Tools" }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors
                  ${activeTab === tab.id 
                    ? "bg-white text-[#1F3A5F] shadow-sm" 
                    : "text-gray-500 hover:text-gray-700"
                  }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Settings Tab */}
          {activeTab === "settings" && settings && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-[#F7F9FB] to-white rounded-xl p-5 border">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-[#1F3A5F]">Public Web Mode</h3>
                    <p className="text-sm text-gray-500">
                      Control access level for public website visitors
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${settings.public_web_mode === "full" ? "text-green-600" : "text-gray-400"}`}>
                      FULL
                    </span>
                    <Switch
                      checked={settings.public_web_mode === "limited"}
                      onCheckedChange={(checked) => 
                        updateSettings({ public_web_mode: checked ? "limited" : "full" })
                      }
                      data-testid="mode-toggle"
                    />
                    <span className={`text-sm font-medium ${settings.public_web_mode === "limited" ? "text-amber-600" : "text-gray-400"}`}>
                      LIMITED
                    </span>
                  </div>
                </div>
                
                <div className={`p-4 rounded-lg ${settings.public_web_mode === "full" ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
                  {settings.public_web_mode === "full" ? (
                    <div className="text-green-700 text-sm">
                      <strong>FULL Mode Active:</strong> All visitors have unlimited access to all features.
                    </div>
                  ) : (
                    <div className="text-amber-700 text-sm">
                      <strong>LIMITED Mode Active:</strong> Visitors get {settings.daily_limit} free analysis per day with basic tones only.
                    </div>
                  )}
                </div>
              </div>

              {settings.public_web_mode === "limited" && (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-5 border">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Daily Free Limit
                    </label>
                    <Select
                      value={String(settings.daily_limit)}
                      onValueChange={(value) => updateSettings({ daily_limit: parseInt(value) })}
                    >
                      <SelectTrigger className="w-32 rounded-xl">
                        <SelectValue placeholder="Select limit" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 10, 15, 20, 50].map((num) => (
                          <SelectItem key={num} value={String(num)}>
                            {num} {num === 1 ? 'use' : 'uses'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-2">
                      Number of free analyses per user per day
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Whitelist Tab */}
          {activeTab === "whitelist" && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <Input
                  placeholder="Email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="flex-1 rounded-xl"
                  data-testid="whitelist-email-input"
                />
                <Input
                  placeholder="Name (optional)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-40 rounded-xl"
                />
                <Button
                  onClick={addWhitelistEntry}
                  className="rounded-xl bg-[#4FB3A5] hover:bg-[#3d9a8d]"
                  data-testid="add-whitelist-button"
                >
                  <Plus size={18} />
                </Button>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-1 max-h-64 overflow-y-auto">
                {whitelist.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No whitelisted emails yet
                  </div>
                ) : (
                  <div className="space-y-1">
                    {whitelist.map((entry) => (
                      <div
                        key={entry.email}
                        className="flex items-center justify-between p-3 bg-white rounded-lg"
                      >
                        <div>
                          <span className="font-medium text-gray-700">{entry.email}</span>
                          {entry.name && (
                            <span className="ml-2 text-sm text-gray-500">({entry.name})</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeWhitelistEntry(entry.email)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invite Codes Tab */}
          {activeTab === "codes" && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <Input
                  placeholder="Description (e.g., Influencer - John)"
                  value={newCodeName}
                  onChange={(e) => setNewCodeName(e.target.value)}
                  className="flex-1 rounded-xl"
                  data-testid="invite-code-name-input"
                />
                <Input
                  type="number"
                  placeholder="Max uses"
                  min={1}
                  value={newCodeMaxUses}
                  onChange={(e) => setNewCodeMaxUses(parseInt(e.target.value) || 1)}
                  className="w-24 rounded-xl"
                />
                <Button
                  onClick={createInviteCode}
                  className="rounded-xl bg-[#4FB3A5] hover:bg-[#3d9a8d]"
                  data-testid="create-code-button"
                >
                  <Plus size={18} className="mr-1" />
                  Create
                </Button>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-1 max-h-64 overflow-y-auto">
                {inviteCodes.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No invite codes yet
                  </div>
                ) : (
                  <div className="space-y-1">
                    {inviteCodes.map((code) => (
                      <div
                        key={code.code}
                        className="flex items-center justify-between p-3 bg-white rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                            {code.code}
                          </code>
                          {code.name && (
                            <span className="text-sm text-gray-500">{code.name}</span>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {code.uses}/{code.max_uses} used
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyCode(code.code)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <Copy size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteInviteCode(code.code)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === "stats" && stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">{stats.today.total_uses}</div>
                <div className="text-sm text-gray-500">Today's Analyses</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-4 border border-green-100">
                <div className="text-2xl font-bold text-green-600">{stats.today.unique_users}</div>
                <div className="text-sm text-gray-500">Unique Users Today</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 border border-purple-100">
                <div className="text-2xl font-bold text-purple-600">{stats.totals.analyses}</div>
                <div className="text-sm text-gray-500">Total Analyses</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-4 border border-amber-100">
                <div className="text-2xl font-bold text-amber-600">{stats.totals.code_redemptions}</div>
                <div className="text-sm text-gray-500">Code Redemptions</div>
              </div>
            </div>
          )}

          {/* Tools Tab */}
          {activeTab === "tools" && (
            <div className="space-y-4">
              {/* Banner Controls */}
              <div className="bg-gradient-to-br from-[#F7F9FB] to-white rounded-xl p-5 border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#4FB3A5] flex items-center justify-center">
                    <Bell className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F3A5F]">Announcement Banner</h3>
                    <p className="text-sm text-gray-500">
                      Control the launch announcement banner visibility
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => {
                      sessionStorage.removeItem("sayitbetter_banner_dismissed");
                      toast.success("Banner reset! Refresh the page to see it.");
                    }}
                    className="rounded-xl bg-[#4FB3A5] hover:bg-[#3d9a8d] gap-2"
                    data-testid="reset-banner-btn"
                  >
                    <RotateCcw size={16} />
                    Show Banner Again
                  </Button>
                  <Button
                    onClick={() => {
                      sessionStorage.setItem("sayitbetter_banner_dismissed", "true");
                      toast.success("Banner hidden! Refresh the page to apply.");
                    }}
                    variant="outline"
                    className="rounded-xl gap-2"
                    data-testid="hide-banner-btn"
                  >
                    <EyeOff size={16} />
                    Hide Banner
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500 mt-3">
                  Note: Banner state is stored per browser session. Closing the browser resets it.
                </p>
              </div>

              {/* Popup Controls */}
              <div className="bg-gradient-to-br from-[#F7F9FB] to-white rounded-xl p-5 border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#7BC4A4] flex items-center justify-center">
                    <Bell className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F3A5F]">Early Access Popup</h3>
                    <p className="text-sm text-gray-500">
                      Control the 10-second popup modal visibility
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => {
                      sessionStorage.removeItem("sayitbetter_popup_shown");
                      toast.success("Popup reset! Refresh and wait 10 seconds to see it.");
                    }}
                    className="rounded-xl bg-[#7BC4A4] hover:bg-[#6ab393] gap-2"
                    data-testid="reset-popup-btn"
                  >
                    <RotateCcw size={16} />
                    Show Popup Again
                  </Button>
                  <Button
                    onClick={() => {
                      sessionStorage.setItem("sayitbetter_popup_shown", "true");
                      toast.success("Popup disabled for this session.");
                    }}
                    variant="outline"
                    className="rounded-xl gap-2"
                    data-testid="hide-popup-btn"
                  >
                    <EyeOff size={16} />
                    Disable Popup
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500 mt-3">
                  Note: Popup appears once after 10 seconds for new visitors.
                </p>
              </div>

              {/* Cache Controls */}
              <div className="bg-gradient-to-br from-[#F7F9FB] to-white rounded-xl p-5 border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#1F3A5F] flex items-center justify-center">
                    <RefreshCw className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F3A5F]">Clear Local Data</h3>
                    <p className="text-sm text-gray-500">
                      Reset all locally stored preferences and session data
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={() => {
                    sessionStorage.clear();
                    localStorage.removeItem("sib_session_id");
                    toast.success("Local data cleared! Refresh to apply.");
                  }}
                  variant="outline"
                  className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-2"
                  data-testid="clear-cache-btn"
                >
                  <Trash2 size={16} />
                  Clear Session Data
                </Button>
                
                <p className="text-xs text-gray-500 mt-3">
                  This will reset banner state, session ID, and other local preferences.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
